import { useLang } from 'hooks/lang';

import { TaskWithSub, User, actions, taskStatusesConfig } from 'entities';

import { Column, Row, Button, Dot } from 'components/Blocks';
import { PropertyWithIcon } from 'components/PropertyWithIcon';
import { OwnerSelect } from 'components/Inputs';

import {
  ClockIcon,
  EarthIcon,
  LocationMapIcon,
  UserPlusIcon,
  UserSquareIcon,
} from 'components/Icons';

import { useMultiStateV2 } from 'hooks';
import { useProject } from 'providers/Project';
import { useLocalPendingEntries } from 'providers/PendingActions';
import { useTeam } from 'providers/Team';

import { ContentCard, ProjectLink, TwoColumnWrapper } from '../components';
import { TeamTasks } from './TeamTasks';

import { formatDate } from 'utils/date';
import { listGet, listUpdate } from 'utils/array';
import { getCountryLabel } from 'utils/countries';
import { UserAvatar } from 'components/UserAvatar';
import { TeamTag } from 'pages/Projects/ProjectProfile/TeamTag';

type State = Record<string, User | boolean>;

const loadingKey = (task: string | { id: string }): string =>
  `loading_${typeof task === 'string' ? task : task.id}`;

export function TaskAssign() {
  const { t } = useLang();

  const [state, { dispatch }] = useMultiStateV2({} as State);

  const { teamUsers } = useTeam();
  const { projects, updateProject } = useProject();

  const unAssignedTasks = useLocalPendingEntries('UNASSIGNED_TASKS');

  const assignTask = actions.projects.assignTask.use({
    onSuccess: (task) =>
      updateProject(task.projectId, (old) => ({
        ...old,
        tasks: listUpdate(old.tasks, task),
      })),
  });

  const handleSaveClick = async (task: TaskWithSub): Promise<void> => {
    const owner = state[task.id];

    if (!owner) return;

    dispatch({ [loadingKey(task)]: true });

    await assignTask.execute({
      owner: (owner as User).id,
      project: task.projectId,
      task: task.id,
    });

    dispatch({ [loadingKey(task)]: false });
  };

  return (
    <TwoColumnWrapper>
      <Column>
        {unAssignedTasks.map(({ expectedDate, ...task }) => (
          <ContentCard
            key={task.id}
            header={
              <>
                <Dot color={taskStatusesConfig.unassigned.color} size={16} />

                <span>{task.name}</span>

                <ProjectLink id={task.projectId} text={`in ${projects[task.projectId].name}`} />

                <span className="ml-auto">
                  <TeamTag
                    team={projects[task.projectId].teamResponsible}
                    color="primary-foreground"
                  />
                </span>
              </>
            }
            content={
              <Row justifyContent="space-between" fullWidth flexWrap="wrap" alignItems="flex-start">
                <Column>
                  <PropertyWithIcon
                    icon={<UserAvatar user={projects[task.projectId].owner} size={30} />}
                    name={t('owner')}
                    value={projects[task.projectId].owner.name}
                  />

                  <PropertyWithIcon
                    icon={<UserSquareIcon />}
                    name={t('client')}
                    value={projects[task.projectId].clientName}
                  />

                  <PropertyWithIcon
                    icon={<LocationMapIcon />}
                    name={t('client-origin')}
                    value={projects[task.projectId].clientOrigin}
                  />

                  <PropertyWithIcon
                    icon={<ClockIcon />}
                    name={t('expected-date')}
                    value={expectedDate ? formatDate(expectedDate) : '(Waiting on PM)'}
                  />

                  <PropertyWithIcon
                    icon={<EarthIcon />}
                    name={t('countries')}
                    value={projects[task.projectId].countries
                      .map((code) => getCountryLabel(code))
                      .join(', ')}
                  />
                </Column>

                <Column className="phone:w-full phone:items-center">
                  <OwnerSelect
                    value={(state[task.id] as User)?.id || ''}
                    onChange={(value) => dispatch({ [task.id]: listGet(teamUsers, value) })}
                  />

                  <Button
                    variant="outlined"
                    onClick={() => handleSaveClick(task)}
                    loading={!!state[loadingKey(task)]}
                    disabled={!state[task.id]}
                  >
                    <UserPlusIcon size={20} />
                    {t('save')}
                  </Button>
                </Column>
              </Row>
            }
          />
        ))}
      </Column>

      <TeamTasks />
    </TwoColumnWrapper>
  );
}
